import React, { useState } from "react";

import { PlpSparePartFinderProductLevel } from "@swo/storefront";
import { useAsyncValue } from "../../../hooks";
import { PlpSitemapLinker } from "../sitemap/plp-sitemap";
import { SparePartsLoadingScreen } from "./spare-parts-loading-screen";
import { SparePartsNavigation } from "../shared/spare-parts-navigation";
import { SparePartsFinderBottomNavigation } from "../shared/spare-parts-finder-bottom-navigation";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import { SwarovskiLink } from "../../../components/_shared-components/ui-elements/SwarovskiLink";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import cn from "classnames";
import { CSS_MQ } from "../../../styles/breakpoints";
import { LeftHandSideImage } from "../shared/left-hand-side-image";
import { SparePartsProductListingPageTranslation } from "@swo/storefront";

export const SelectProductLevel: React.FC<{
  pathname: string;
  fetcher: ({ subfamilyKey }: { subfamilyKey: string }) => Promise<PlpSparePartFinderProductLevel>;
  sitemapLinker: PlpSitemapLinker;
  subfamilyKey: string;
  translations: SparePartsProductListingPageTranslation;
  categoryKey: string;
}> = props => {
  const { subfamilyKey } = props;

  return useAsyncValue(() => props.fetcher({ subfamilyKey }), { dataKey: subfamilyKey }).fold({
    onLoading: () => SparePartsLoadingScreen,
    onError: err => <span>Error</span>,
    onLoaded: props2 => <PageComponent {...props} {...props2} />,
  });
};

const PageComponent: ReactTFC<
  {
    categoryKey: string;
    subfamilyKey: string;
    sitemapLinker: PlpSitemapLinker;
    translations: SparePartsProductListingPageTranslation;
  } & PlpSparePartFinderProductLevel
> = props => {
  const { subfamilyKey, categoryKey, sitemapLinker, selectedCategory, selectedSubfamily, products, level } = props;
  const backLink = sitemapLinker("category", { categoryKey });

  const [selectedIdx, setSelectedIdx] = useState(0);

  const selectedProduct = products[selectedIdx]!;

  return (
    <>
      <SparePartsNavigation
        BackNavigation={<SparePartsFinderBottomNavigation link={backLink} translations={props.translations} />}
        category={selectedCategory}
        subfamily={selectedSubfamily}
        translations={props.translations}
      />

      <ContentContainer>
        {products.length > 0 ? (
          <div
            css={css`
              display: grid;
              gap: 20px;

              ${CSS_MQ.atLeastMd} {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: 40px;
              }
            `}
          >
            <LeftHandSideImage
              image={selectedProduct.image}
              attachStyles={css`
                &:not(.visible-sm) {
                  ${CSS_MQ.atMostSm} {
                    display: none;
                  }
                }
              `}
            />

            <div>
              <ul
                css={css`
                  display: grid;
                  gap: 10px;

                  ${CSS_MQ.atLeastSm} {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 20px;
                  }
                `}
              >
                {products.map(({ productKey, name, image }, idx) => (
                  <div
                    key={productKey}
                    className={cn({ selected: idx === selectedIdx })}
                    onMouseOver={() => setSelectedIdx(idx)}
                    css={css`
                      border: 1px solid ${CSS_COLORS.Grey2};
                      border-radius: 8px;

                      color: ${CSS_COLORS.Black};

                      a {
                        color: inherit;
                      }

                      text-align: center;

                      &:hover {
                        border: 1px solid ${CSS_COLORS.Green};
                        box-shadow: 0 0 5px ${CSS_COLORS.Green};
                      }
                    `}
                  >
                    <SwarovskiLink
                      link={sitemapLinker("product", { subfamilyKey, categoryKey, productKey })}
                      isInternal={true}
                      styled={"no-decoration"}
                      scrollToTop={true}
                      attachStyles={css`
                        display: block;

                        padding: 12px;

                        ${CSS_MQ.atLeastSm} {
                          padding: 20px;
                        }
                      `}
                    >
                      {name}
                    </SwarovskiLink>
                  </div>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <span>No products.</span>
        )}

        <Spacer size={"md"} />

        <SparePartsFinderBottomNavigation link={backLink} centered={true} translations={props.translations} />
      </ContentContainer>
    </>
  );
};
